import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";

import { SimpleDialogComponent } from "../components/dialog/simple-dialog.component";

/**
 * Explains navigation dead ends to the user in a single-button dialog, then lets
 * the caller redirect somewhere valid.
 *
 * Three cases, one per i18n block:
 * - `pageNotFound` — the URL matches no route (notFoundGuard, before /kpi);
 * - `accessDenied` — authenticated but below the required role (roleGuard, before /kpi);
 * - `itemNotFound` — the page exists but the deep-linked record does not (the
 *   page-level `:id` / `:graphSlug` fallbacks, which stay in their section).
 */
@Injectable({
  providedIn: "root"
})
export class NavigationNoticeService {
  /**
   * Whether a notice is currently on screen. Set synchronously so a burst of
   * triggers (a paramMap re-emit, two redirects in a row) cannot stack dialogs.
   */
  private isOpen: boolean = false;

  constructor(
    private dialog: MatDialog,
    private translate: TranslateService
  ) {}

  /** The URL matches no route at all. */
  public pageNotFound(): void {
    this.open("notFound");
  }

  /** The route exists but the user's profile is below the role it requires. */
  public accessDenied(): void {
    this.open("accessDenied");
  }

  /** The page exists but the record its `:id` addresses does not. */
  public itemNotFound(): void {
    this.open("itemNotFound");
  }

  /**
   * Opens the notice for an i18n block exposing `title` / `description` /
   * `confirm`. Uses `translate.get` rather than `instant`: a cold-start deep link
   * runs the router's first navigation while the catalog is still loading, and
   * `instant` would render the raw keys.
   * @param block {string} The i18n block name (`notFound`, `accessDenied`, …).
   */
  private open(block: string): void {
    if (this.isOpen) {
      return;
    }
    this.isOpen = true;

    const titleKey: string = `${block}.title`;
    const textKey: string = `${block}.description`;
    const confirmKey: string = `${block}.confirm`;

    this.translate.get([titleKey, textKey, confirmKey]).subscribe(
      (translations) => {
        const dialogRef = this.dialog.open(SimpleDialogComponent, {
          data: {
            title: translations[titleKey],
            text: translations[textKey],
            yes: translations[confirmKey]
          }
        });
        dialogRef.afterClosed().subscribe(() => {
          this.isOpen = false;
        });
      },
      () => {
        // Catalog failed to load: never leave the notice latched shut.
        this.isOpen = false;
      }
    );
  }
}
